import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";

export default function NotificationsDropdown() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) { console.error("Notifications fetch failed:", e); }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, []);

  // close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { console.error("markRead failed:", e); }
  };

  const markAll = async () => {
    try {
      await api.put("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { console.error("markAll failed:", e); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
        data-testid="notifications-btn"
        aria-label={t("notifications.title")}
      >
        <Bell size={18}/>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-80 max-w-[calc(100vw-1rem)] rounded-md bg-slate-900 border border-slate-700 shadow-xl z-50" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div className="font-display font-semibold text-slate-100 text-sm">{t("notifications.title")}</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="notifications-mark-all">
                <CheckCheck size={12}/> {t("notifications.markAll")}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
                <Inbox size={24} className="text-slate-700"/>
                {t("notifications.empty")}
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-slate-800 last:border-0 hover:bg-slate-800/50 ${n.read ? "" : "bg-blue-500/5"}`}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      {n.link ? (
                        <Link to={n.link} onClick={() => { markRead(n.id); setOpen(false); }} className="block">
                          <div className="text-sm text-slate-100 font-medium break-words">{n.title}</div>
                          {n.body && <div className="text-xs text-slate-400 mt-1 break-words [overflow-wrap:anywhere]">{n.body}</div>}
                        </Link>
                      ) : (
                        <div>
                          <div className="text-sm text-slate-100 font-medium break-words">{n.title}</div>
                          {n.body && <div className="text-xs text-slate-400 mt-1 break-words [overflow-wrap:anywhere]">{n.body}</div>}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-600 mt-1 font-mono-data">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                    </div>
                    {!n.read && (
                      <button onClick={() => markRead(n.id)} className="text-slate-500 hover:text-blue-400 p-1 flex-shrink-0" title={t("common.markRead")}>
                        <Check size={14}/>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
